import ImportWhatsAppProgress from "../../components/ImportWhatsAppProgress";
import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	Pencil,
	Trash2,
	CheckCircle2,
	QrCode,
	WifiOff,
	Wifi,
	Loader2,
	AlertTriangle,
	MessageCircle,
	Share2,
	Plus,
	ChevronDown,
	Send,
	Globe,
	KeyRound,
	History,
} from "lucide-react";
import { FacebookIcon, InstagramIcon } from "../../components/MetaIcons";
import NumeroComBandeira from "../../components/NumeroComBandeira";
import RestaurarConversasModal from "../../components/RestaurarConversasModal";
import ImportacaoEmAndamento from "../../components/ImportacaoEmAndamento";
import { ehTelefone } from "../../helpers/identificadorDoContato";
import { rotuloDoCanal } from "../../helpers/canalDoTicket";

import { Button } from "../../components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { Card } from "../../components/ui/card";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import MetaConnectModal from "../../components/MetaConnectModal";
import TelegramConnectModal from "../../components/TelegramConnectModal";
import WebchatConnectModal from "../../components/WebchatConnectModal";
import ZernioConnectModal from "../../components/ZernioConnectModal";
import QrcodeModal from "../../components/QrcodeModal";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const ehCanalMeta = (whatsApp) =>
	["facebook", "instagram", "whatsapp_official"].includes(whatsApp.channel);

// Instagram, Telegram, X, Reddit, Bluesky, Discord... — o canal vem como
// "zernio:<rede>", porque a Zernio ganha rede nova sem aviso e um valor fixo
// por rede obrigaria a mexer aqui a cada uma.
const ehCanalZernio = (whatsApp) =>
	String(whatsApp.channel || "").startsWith("zernio:");

// Ícone do canal ao lado do nome: sem ele, uma página do Facebook e um número
// de WhatsApp ficam indistinguíveis na lista.
const IconeDoCanal = ({ channel }) => {
	if (String(channel || "").startsWith("zernio:")) {
		const rede = String(channel).slice("zernio:".length);

		if (rede === "instagram") {
			return <InstagramIcon className="h-4 w-4 shrink-0 text-[#e1306c]" />;
		}

		if (rede === "facebook") {
			return <FacebookIcon className="h-4 w-4 shrink-0 text-[#1877f2]" />;
		}

		// As demais redes não têm ícone próprio aqui; o símbolo de
		// compartilhamento ao menos separa "veio de rede social" de "é WhatsApp".
		return <Share2 className="h-4 w-4 shrink-0 text-sky-500" />;
	}

	if (channel === "facebook") {
		return <FacebookIcon className="h-4 w-4 shrink-0 text-[#1877f2]" />;
	}

	if (channel === "instagram") {
		return <InstagramIcon className="h-4 w-4 shrink-0 text-[#e1306c]" />;
	}

	if (channel === "telegram") {
		return <Send className="h-4 w-4 shrink-0 text-[#229ED9]" />;
	}

	if (channel === "webchat") {
		return <Globe className="h-4 w-4 shrink-0 text-indigo-500" />;
	}

	// O oficial é WhatsApp também, mas fala pela Cloud API: o tom mais escuro
	// separa os dois sem inventar um símbolo que ninguém reconhece.
	return (
		<MessageCircle
			className={`h-4 w-4 shrink-0 ${
				channel === "whatsapp_official"
					? "text-emerald-700"
					: "text-emerald-500"
			}`}
		/>
	);
};

const CustomToolTip = ({ title, content, children }) => {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>{children}</TooltipTrigger>
				<TooltipContent className="max-w-[280px] text-center">
					<p className="font-medium">{title}</p>
					{content && <p className="text-muted-foreground">{content}</p>}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};

const Connections = () => {
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	// Conexão com a janela de restauração de histórico aberta.
	const [conexaoParaRestaurar, setConexaoParaRestaurar] = useState(null);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	// Canal da Meta escolhido no menu de adicionar. null = modal fechado.
	const [canalMeta, setCanalMeta] = useState(null);
	const [telegramModalOpen, setTelegramModalOpen] = useState(false);
	// Guarda o id da conexão de webchat sendo editada, ou true para criar uma
	// nova: é o mesmo modal, e ele precisa saber qual dos dois é.
	const [webchatModal, setWebchatModal] = useState(null);
	const [zernioModalOpen, setZernioModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async (whatsAppId) => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async (whatsAppId) => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, []);

	const handleOpenQrModal = (whatsApp) => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, []);

	const handleEditWhatsApp = (whatsApp) => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = (whatsApp) => {
		// Messenger e Direct falam pela Graph API: não há QR para ler nem sessão
		// de celular para reconectar.
		if (ehCanalMeta(whatsApp)) {
			return (
				<span className="text-sm text-muted-foreground">
					Conectado pela Meta
				</span>
			);
		}

		// O bot do Telegram também não tem sessão: o que o mantém no ar é o
		// webhook, registrado quando a conexão foi criada.
		if (whatsApp.channel === "telegram") {
			return (
				<span className="text-sm text-muted-foreground">
					Bot conectado
				</span>
			);
		}

		// O widget está sempre no ar; o que a pessoa vem fazer aqui é pegar o
		// script de novo ou mudar a cor — por isso o botão leva à configuração,
		// e não a uma reconexão que não existe.
		if (whatsApp.channel === "webchat") {
			return (
				<Button
					size="sm"
					variant="outline"
					onClick={() => setWebchatModal(whatsApp.id)}
				>
					Script e aparência
				</Button>
			);
		}

		// Idem para a Zernio: quem conecta e reconecta a conta é o painel dela.
		if (ehCanalZernio(whatsApp)) {
			return (
				<span className="text-sm text-muted-foreground">
					Conectado pela Zernio
				</span>
			);
		}

		return (
			<div className="flex flex-wrap items-center gap-2">
				{whatsApp.status === "qrcode" && (
					<Button size="sm" onClick={() => handleOpenQrModal(whatsApp)}>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="sm"
							variant="outline"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" || whatsApp.restoringHistory) &&
					whatsApp.channel === "whatsapp" && (
						// Traz as conversas que já estavam no aparelho. Antes isso só
						// acontecia no momento da conexão: quem esqueceu de marcar a
						// opção tinha de desconectar e ler o QR de novo, derrubando o
						// atendimento em andamento.
						//
						// Também aparece com a conexão fora do ar quando ficou uma
						// restauração pendente: ela recomeça sozinha na reconexão, e
						// exigir "conectado" para abrir o modal deixava a pessoa sem
						// nenhum caminho para cancelá-la.
						<Button
							size="sm"
							variant="outline"
							className="gap-1.5"
							onClick={() => setConexaoParaRestaurar(whatsApp)}
							title={
								whatsApp.restoringHistory
									? "Há uma restauração de histórico pendente nesta conexão"
									: "Buscar no aparelho as conversas anteriores"
							}
						>
							<History className="h-3.5 w-3.5" />
							{whatsApp.restoringHistory
								? "Restauração pendente"
								: "Restaurar conversas"}
						</Button>
					)}

				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Button
						size="sm"
						variant="outline"
						onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && (
					<Button size="sm" variant="outline" disabled>
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</div>
		);
	};

	const renderStatusToolTips = (whatsApp) => {
		return (
			<div className="flex items-center justify-center">
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<WifiOff className="h-5 w-5 text-muted-foreground" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<QrCode className="h-5 w-5 text-primary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<Wifi className="h-5 w-5 text-emerald-500" />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<AlertTriangle className="h-5 w-5 text-amber-500" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				onCreated={handleOpenQrModal}
			/>
			<MetaConnectModal
				open={Boolean(canalMeta)}
				canal={canalMeta}
				onClose={() => setCanalMeta(null)}
			/>
			<TelegramConnectModal
				open={telegramModalOpen}
				onClose={() => setTelegramModalOpen(false)}
			/>
			<WebchatConnectModal
				open={Boolean(webchatModal)}
				whatsAppId={webchatModal === true ? null : webchatModal}
				onClose={() => setWebchatModal(null)}
			/>
			<RestaurarConversasModal
				aberto={Boolean(conexaoParaRestaurar)}
				conexao={conexaoParaRestaurar}
				aoFechar={() => setConexaoParaRestaurar(null)}
			/>
			<ZernioConnectModal
				open={zernioModalOpen}
				onClose={() => setZernioModalOpen(false)}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					{/* Um botão só, com o canal escolhido no menu. Botão por canal
					    lado a lado enchia o cabeçalho e não cabia no celular — e
					    a escolha do canal é feita uma vez, não toda hora. */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button>
								<Plus className="h-4 w-4" />
								{i18n.t("connections.buttons.add")}
								<ChevronDown className="h-4 w-4 opacity-70" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-72">
							<DropdownMenuLabel>WhatsApp</DropdownMenuLabel>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={handleOpenWhatsAppModal}
							>
								<span className="flex items-center gap-2 font-medium">
									<MessageCircle className="h-4 w-4 text-emerald-500" />
									QR Code
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Lê o código no celular. É o caminho de sempre.
								</span>
							</DropdownMenuItem>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setCanalMeta("whatsapp_official")}
							>
								<span className="flex items-center gap-2 font-medium">
									<MessageCircle className="h-4 w-4 text-emerald-700" />
									API Oficial (Cloud API)
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Número aprovado pela Meta, sem depender do celular.
								</span>
							</DropdownMenuItem>

							<DropdownMenuSeparator />
							<DropdownMenuLabel>Meta</DropdownMenuLabel>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setCanalMeta("facebook")}
							>
								<span className="flex items-center gap-2 font-medium">
									<FacebookIcon className="h-4 w-4 text-[#1877f2]" />
									Facebook
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Mensagens do Messenger da página.
								</span>
							</DropdownMenuItem>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setCanalMeta("instagram")}
							>
								<span className="flex items-center gap-2 font-medium">
									<InstagramIcon className="h-4 w-4 text-[#e1306c]" />
									Instagram
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Direct da conta comercial.
								</span>
							</DropdownMenuItem>

							<DropdownMenuSeparator />
							<DropdownMenuLabel>Outros canais</DropdownMenuLabel>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setTelegramModalOpen(true)}
							>
								<span className="flex items-center gap-2 font-medium">
									<Send className="h-4 w-4 text-[#229ED9]" />
									Telegram
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Um bot criado no @BotFather. Só o token.
								</span>
							</DropdownMenuItem>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setWebchatModal(true)}
							>
								<span className="flex items-center gap-2 font-medium">
									<Globe className="h-4 w-4 text-indigo-500" />
									Chat do site
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									Uma bolha de conversa no seu site, com um script.
								</span>
							</DropdownMenuItem>
							<DropdownMenuItem
								className="flex-col items-start gap-0.5"
								onSelect={() => setZernioModalOpen(true)}
							>
								<span className="flex items-center gap-2 font-medium">
									<KeyRound className="h-4 w-4 text-violet-500" />
									Zernio
								</span>
								<span className="pl-6 text-xs text-muted-foreground">
									WhatsApp Oficial, Instagram, Facebook e Telegram numa chave só.
								</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{/* Acima da tabela, e não dentro do modal de uma conexão: enquanto
				    a importação estiver rodando ela é o assunto da tela. Quem
				    acabou de parear não sabe que existe um modal para abrir. */}
				<ImportacaoEmAndamento whatsApps={whatsApps} />
				{/* Tabela: do tablet para cima. Seis colunas dão 706px, e no
				    celular a coluna de ações — a que se usa — ficava fora da
				    tela, atrás de uma rolagem lateral que ninguém faz. */}
				<Card className="hidden border-none shadow-xs sm:block">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="text-center">
									{i18n.t("connections.table.name")}
								</TableHead>
								<TableHead className="text-center">
									{i18n.t("connections.table.status")}
								</TableHead>
								<TableHead className="text-center">
									{i18n.t("connections.table.session")}
								</TableHead>
								<TableHead className="text-center">
									{i18n.t("connections.table.lastUpdate")}
								</TableHead>
								<TableHead className="text-center">
									{i18n.t("connections.table.default")}
								</TableHead>
								<TableHead className="text-center">
									{i18n.t("connections.table.actions")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRowSkeleton columns={6} />
							) : (
								whatsApps?.length > 0 &&
								whatsApps.map((whatsApp) => (
									<TableRow key={whatsApp.id}>
										<TableCell className="text-center font-medium">
											<div className="flex items-center justify-center gap-2">
												<IconeDoCanal channel={whatsApp.channel} />
												{whatsApp.name}
											</div>
											{/* De qual rede e esta conexao. O nome da conta sozinho nao
											    diz: duas contas da Meta viram duas linhas parecidas,
											    com um icone de 16px como unica pista. */}
											<div className="mt-0.5 text-xs font-normal text-muted-foreground">
												{rotuloDoCanal(whatsApp.channel)}
											</div>
											{/* Com várias conexões, o nome sozinho não diz qual
											    número está ali — e é o número que a equipe usa
											    para se orientar. */}
											{whatsApp.number && (
												<div className="mt-0.5 text-xs font-normal tabular-nums text-muted-foreground">
													{ehTelefone(whatsApp.number, false, whatsApp.channel) ? (
															<NumeroComBandeira numero={whatsApp.number} />
														) : (
															/* O bot do Telegram se identifica por @usuario, e o
															   widget de site por nada: uma bandeira de pais
															   deduzida dos digitos desses valores e invencao. */
															<span>{whatsApp.number}</span>
														)}
												</div>
											)}
										</TableCell>
										<TableCell className="text-center">
											{renderStatusToolTips(whatsApp)}
										</TableCell>
										<TableCell className="text-center">
											{renderActionButtons(whatsApp)}
<ImportWhatsAppProgress conexao={whatsApp} />
										</TableCell>
										<TableCell className="text-center text-sm text-muted-foreground">
											{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
										</TableCell>
										<TableCell className="text-center">
											{whatsApp.isDefault && (
												<div className="flex items-center justify-center">
													<CheckCircle2 className="h-5 w-5 text-emerald-500" />
												</div>
											)}
										</TableCell>
										<TableCell className="text-center">
											<div className="flex items-center justify-center gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() =>
														handleOpenConfirmationModal("delete", whatsApp.id)
													}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</Card>

				{/* Conexões no celular.
				    Cada conexão é um cartão: quem é, se está no ar e o que dá
				    para fazer com ela. A situação vem primeiro porque é o que
				    se vem conferir aqui — conexão caída é atendimento parado. */}
				<div className="space-y-3 sm:hidden">
					{loading && (
						<div className="rounded-xl border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
							Carregando...
						</div>
					)}

					{!loading && !whatsApps?.length && (
						<div className="rounded-xl border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
							Nenhuma conexão ainda. Toque em “{i18n.t("connections.buttons.add")}”
							para ligar um número.
						</div>
					)}

					{!loading &&
						whatsApps?.map((whatsApp) => (
							<div
								key={whatsApp.id}
								className="space-y-3 rounded-xl border bg-card p-3"
							>
								<div className="flex items-start gap-2">
									<div className="mt-0.5 shrink-0">
										<IconeDoCanal channel={whatsApp.channel} />
									</div>

									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<span className="truncate font-semibold">
												{whatsApp.name}
											</span>
											<span className="shrink-0 text-xs text-muted-foreground">
												{rotuloDoCanal(whatsApp.channel)}
											</span>
											{whatsApp.isDefault && (
												<span
													title="Conexão padrão: é por ela que saem os envios automáticos."
													className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600"
												>
													<CheckCircle2 className="h-3 w-3" />
													Padrão
												</span>
											)}
										</div>

										{whatsApp.number && (
											<p className="mt-0.5 truncate text-sm tabular-nums text-muted-foreground">
												{ehTelefone(whatsApp.number, false, whatsApp.channel) ? (
															<NumeroComBandeira numero={whatsApp.number} />
														) : (
															/* O bot do Telegram se identifica por @usuario, e o
															   widget de site por nada: uma bandeira de pais
															   deduzida dos digitos desses valores e invencao. */
															<span>{whatsApp.number}</span>
														)}
											</p>
										)}
									</div>

									<div className="flex shrink-0 items-center">
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9"
											aria-label={`Editar ${whatsApp.name}`}
											onClick={() => handleEditWhatsApp(whatsApp)}
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 text-destructive hover:text-destructive"
											aria-label={`Excluir ${whatsApp.name}`}
											onClick={() =>
												handleOpenConfirmationModal("delete", whatsApp.id)
											}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</div>

								<div className="flex flex-wrap items-center gap-2 border-t pt-3">
									{renderStatusToolTips(whatsApp)}
									{renderActionButtons(whatsApp)}
<ImportWhatsAppProgress conexao={whatsApp} />
								</div>

								<p className="text-xs text-muted-foreground">
									Atualizado em{" "}
									{format(parseISO(whatsApp.updatedAt), "dd/MM/yy 'às' HH:mm")}
								</p>
							</div>
						))}
				</div>
			</div>
		</MainContainer>
	);
};

export default Connections;
